import { constructJson, getFirstQuestionOfSlide, useFormFactory } from '@meilleurtaux/form-factory'
import {
  ValidateSlideButtons,
  ButtonSaveForLater,
  Col,
  Container,
  Footer,
  MarketingHulk,
  Mentions,
  Row,
  ThemeProvider,
  useDeviceDetection,
  useTracking,
} from '@meilleurtaux/form-factory-ds'
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import styled from 'styled-components'
import type { IconType } from '@meilleurtaux/form-factory-ds'
import type { Config } from 'type/app'
import Header from 'components/Header'
import InternauteReconnu from 'components/InternauteReconnu'
import SaveAndContinueLater from 'components/SaveAndContinueLater'
import ScrollNavigationIndicator from 'components/ScrollNavigationIndicator'
import Slides from 'components/Slides'
import Stepper from 'components/Stepper'
import TransfertProduit from 'components/TransfertProduit'
import useScrollNavigation from 'hooks/useScrollNavigation'
import useToggle from 'hooks/useToggle'
import { getFromCookie } from 'utils/cookie'
import { handleAbTestEvent } from 'utils/handleAbTest'
import { getInternauteReconnuData } from 'utils/internauteReconnu'
import { getParamCaseInsensitive } from 'utils/params'
import { getPrevHandler } from 'utils/prevButton'
import { getNextButtonLabel } from 'utils/questionManager'
import { SCREEN_M } from 'utils/screens'
import { isScrollNavigationEnabled } from 'utils/slideAnimation'
import { submitStepForm } from 'utils/stepForm'
import {
  getContentContainerStyle,
  getContentRowStyle,
  getWidthStepper,
  showSideStepper,
  showTopProgressBar,
} from 'utils/stepperLayout'
import { FORM_DATA_EXPIRATION_MS, storage } from 'utils/storage'
import { getFont, getMainColor, getSecondaryColor } from 'utils/theme'
import { getUrl } from 'utils/url'
import { getUtmValue, utmList } from 'utils/utm'
import { getAppearanceFromConfigs } from 'utils/widgetMode'

const CtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 36px;
  gap: 16px;

  @media (${SCREEN_M}) {
    margin-top: 52px;
    gap: 0;
  }
`

interface FormProps {
  config?: Config
  setTrackingInformation: (trackingInfo: {
    tunnelId: string
    userSheetId: string
    insukind: string
  }) => void

  // Parametres optionnels pour l'AB Test Auto
  insukindAB?: string
  routeAB?: string
}

export default function Form({
  config,
  insukindAB,
  routeAB,
  setTrackingInformation,
}: Readonly<FormProps>) {
  const {
    initiateForm,
    getExtraInfosForPrefilledResponse,
    appearance,
    currentSlide,
    redirectToTarification,
    meta,
    mentions,
    argumentMarketing,
    template,
    manageParamsForInitialization,
    removeRedirectToTarification,
    saveResponse,
    stepper,
    canPrev,
    goPrev,
    isSlideValid,
  } = useFormFactory(import.meta.env.VITE_API_URL)
  const isWidgetMode =
    import.meta.env.VITE_IS_WIDGET === true || import.meta.env.VITE_IS_WIDGET === 'true'
  const isProduction = import.meta.env.VITE_ENV === 'production'
  const [nextIsLoading, toggleNextIsLoading] = useToggle(false)
  const [saveModalOpened, setSaveModalOpened] = useState(false)
  const { tcReady, trackTunnelLoaded, trackFormAbandoned, updateWidgetConsent } = useTracking()
  const hasTunnelBeenTrackedRef = useRef(false)
  const finalAppearance = getAppearanceFromConfigs(config, appearance)

  let insukind: string | undefined
  let route: string | undefined
  let queryParams: URLSearchParams
  let clientId: string | undefined

  // Toujours appeler les hooks, mais les utiliser conditionnellement
  try {
    const params = useParams()

    insukind = insukindAB || params.insukind || storage.getValue('FF_Insukind') || undefined
    route = routeAB || params.route || storage.getValue('FF_Route') || undefined
    clientId = config && 'client_id' in config ? config.client_id : params.clientId || undefined
  } catch {
    // En cas d'erreur (mode widget), utiliser des paramètres vides
    insukind = insukindAB || storage.getValue('FF_Insukind') || undefined
    route = routeAB || storage.getValue('FF_Route') || undefined
    clientId = config && 'client_id' in config ? config.client_id : undefined
  }

  try {
    const [params] = useSearchParams()
    queryParams = params
  } catch {
    // En cas d'erreur (mode widget), utiliser des paramètres vides
    queryParams = new URLSearchParams()
  }

  const device = useDeviceDetection()

  // Navigation au scroll (desktop, animation verticale) : les boutons restent la voie principale
  const { progress: scrollProgress, direction: scrollDirection } = useScrollNavigation({
    // desktop uniquement ; désactivé en widget : butées et indicateur dépendent du viewport, pas du conteneur du widget
    enabled: isScrollNavigationEnabled({
      device,
      isWidgetMode,
      stepperPosition: appearance?.stepperPosition,
      animationDirection: appearance?.animationDirection,
    }),
    canPrev,
    // le scroll vers la suite est bloqué tant que toutes les questions visibles ne sont pas répondues
    canNext:
      !!currentSlide?.displayNextButton &&
      !nextIsLoading &&
      isSlideValid &&
      !currentSlide.isLastSlide,
    onPrev: goPrev,
    onNext: submitStepForm,
  })

  // On cherche à récupérer l'id de la fiche utilisateur dans l'url ou dans le storage
  const userSheetId =
    getParamCaseInsensitive(queryParams, 'userSheetId') ?? storage.getValue('FF_UserSheetId')
  // On cherche à récupérer l'id du tunnel dans le storage
  const tunnelId = storage.getValue('FF_TunnelId')
  // On cherche à récupérer l'id de la variation dans le storage
  const variationId = storage.getValue('FF_VariationId')
  // On cherche à récupérer le type de produit de la fiche dans le storage
  const insukindUserSheet = storage.getValue('FF_Insukind')
  // On cherche à récupérer l'id client dans le storage
  const clientIdStorage = storage.getValue('FF_ClientId')
  // On cherche à récupérer si on est fromScratch dans les query params
  const fromScratch = getParamCaseInsensitive(queryParams, 'fromScratch') === 'true'

  useEffect(() => {
    if (redirectToTarification) {
      // On enlève le flag pour éviter les boucles de redirection lors du retour sur le formulaire
      removeRedirectToTarification()
      // Redirection vers la page de tarification
      globalThis.location.href = `${getUrl(
        import.meta.env.VITE_ENV,
        'forms',
        appearance,
      )}/FormFactory/SessionLoad?Id=${userSheetId}`
    }

    if (currentSlide) return

    // Function pour construire le json des utms pour l'appel à firstDisplay
    const constructJsonForUtmAndResponse = async (): Promise<string> => {
      const response: { key: string; response: string }[] = []

      for (const utm of utmList) {
        const value = getUtmValue(utm, queryParams, config)
        if (value) response.push({ key: `AFF_${utm}`, response: value })
      }

      const extraInfos = insukind ? await getExtraInfosForPrefilledResponse(insukind) : []
      for (const extraInfo of extraInfos) {
        const value = getParamCaseInsensitive(queryParams, extraInfo)
        if (value) response.push({ key: extraInfo, response: value })
      }

      response.push(
        {
          key: 'AFF_Query',
          response: getFromCookie('aff_query') ?? queryParams.toString(),
        },
        {
          key: 'AFF_referrer',
          response: getFromCookie('aff_referrer') ?? document.referrer,
        },
      )

      return constructJson(response)
    }

    const initiate = async () => {
      const jsonForUtmAndResponse = await constructJsonForUtmAndResponse()

      const initiateFormParams = manageParamsForInitialization({
        insukind,
        route,
        clientId,
        userSheetId,
        tunnelId,
        jsonExtraInformation: jsonForUtmAndResponse,
        insukindUserSheet,
        clientIdStorage,
        internauteReconnu: getInternauteReconnuData(queryParams),
        initialPage: getFromCookie('Initial_page') ?? undefined,
        fromScratch,
      })

      const {
        returnedTunneId,
        returnedVariationId,
        returnedUserSheetId,
        returnedInsukind,
        returnedRoute,
      } = await initiateForm(initiateFormParams)

      // On stocke les informations récupérées dans le storage
      if (
        returnedTunneId &&
        returnedVariationId &&
        returnedUserSheetId &&
        returnedInsukind &&
        returnedRoute
      ) {
        storage.setValue('FF_TunnelId', returnedTunneId.toString(), FORM_DATA_EXPIRATION_MS) // Expiration dans 4 heures
        storage.setValue('FF_VariationId', returnedVariationId.toString(), FORM_DATA_EXPIRATION_MS) // Expiration dans 4 heures
        storage.setValue('FF_UserSheetId', returnedUserSheetId, FORM_DATA_EXPIRATION_MS) // Expiration dans 4 heures
        storage.setValue('FF_Insukind', returnedInsukind.toString(), FORM_DATA_EXPIRATION_MS) // Expiration dans 4 heures
        storage.setValue('FF_Route', returnedRoute.toString(), FORM_DATA_EXPIRATION_MS) // Expiration dans 4 heures
        if (initiateFormParams.clientId) {
          storage.setValue('FF_ClientId', initiateFormParams.clientId, FORM_DATA_EXPIRATION_MS) // Expiration dans 4 heures
        }
      }

      if (
        setTrackingInformation &&
        ((tunnelId !== undefined && tunnelId !== null) ||
          (returnedTunneId !== undefined && returnedTunneId !== null)) &&
        ((userSheetId !== undefined && userSheetId !== null) ||
          (returnedUserSheetId !== undefined && returnedUserSheetId !== null)) &&
        ((insukind !== undefined && insukind !== null) ||
          (returnedInsukind !== undefined && returnedInsukind !== null))
      ) {
        setTrackingInformation({
          tunnelId: tunnelId ?? returnedTunneId?.toString() ?? '',
          userSheetId: userSheetId ?? returnedUserSheetId?.toString() ?? '',
          insukind: insukind ?? returnedInsukind?.toString() ?? '',
        })
      }
    }
    initiate()
  }, [
    initiateForm,
    getExtraInfosForPrefilledResponse,
    removeRedirectToTarification,
    trackTunnelLoaded,
    manageParamsForInitialization,
    setTrackingInformation,
    userSheetId,
    tunnelId,
    variationId,
    insukindUserSheet,
    currentSlide,
    appearance,
    fromScratch,
    redirectToTarification,
    meta,
    tcReady,
    clientIdStorage,
    clientId,
    insukind,
    route,
    config,
    queryParams,
  ])

  // On track le tunnelLoaded lorsque tc est prêt et que le tunnel n'a pas encore été tracké
  useEffect(() => {
    if (!tcReady || hasTunnelBeenTrackedRef.current) return
    if (
      currentSlide === undefined ||
      stepper === undefined ||
      storage.getValue('FF_TunnelId') === null ||
      storage.getValue('FF_VariationId') === null ||
      storage.getValue('FF_UserSheetId') === null ||
      storage.getValue('FF_Insukind') === null ||
      storage.getValue('FF_Route') === null
    )
      return

    const stepperLabel = stepper.find((x) => x.position === currentSlide.stepperPosition)?.label
    const question = getFirstQuestionOfSlide(currentSlide)
    const firstQuestionTitle = question?.label
    const slideFirstQuestionExtraInfo = question?.extraInfo

    // Si on n'a pas réussi à récupérer le label du stepper ou de la première question, on ne track pas l'abandon pour éviter d'envoyer des données incomplètes
    if (!stepperLabel || !firstQuestionTitle || !slideFirstQuestionExtraInfo) return

    trackTunnelLoaded({
      slideFirstQuestionTitle: firstQuestionTitle,
      slideTitle: stepperLabel,
      slideFirstQuestionExtraInfo: slideFirstQuestionExtraInfo,
    })
    hasTunnelBeenTrackedRef.current = true
  }, [currentSlide, stepper, tcReady, trackTunnelLoaded])

  // On track l'abandon du formulaire lorsque l'utilisateur quitte la page
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Si le formulaire n'est pas encore chargé, on ne track pas l'abandon
      if (!currentSlide || !stepper) return

      const stepperLabel = stepper.find((x) => x.position === currentSlide.stepperPosition)?.label
      const question = getFirstQuestionOfSlide(currentSlide)
      const firstQuestionTitle = question?.label
      const slideFirstQuestionExtraInfo = question?.extraInfo

      // Si on n'a pas réussi à récupérer le label du stepper ou de la première question, on ne track pas l'abandon pour éviter d'envoyer des données incomplètes
      if (!stepperLabel || !firstQuestionTitle || !slideFirstQuestionExtraInfo) return

      trackFormAbandoned({
        slideTitle: stepperLabel,
        slideFirstQuestionTitle: firstQuestionTitle,
        slideFirstQuestionExtraInfo: slideFirstQuestionExtraInfo,
      })
    }

    globalThis.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [currentSlide, stepper, trackFormAbandoned])

  // Expose l'API publique du widget vers le site partenaire
  useEffect(() => {
    if (!isWidgetMode) return

    globalThis.formFactory = {
      updateConsent: (status: 'all-on' | 'all-off') => {
        updateWidgetConsent(status)
      },
    }

    return () => {
      delete globalThis.formFactory
    }
  }, [isWidgetMode, updateWidgetConsent])

  useEffect(() => {
    if (userSheetId === null) return

    // Définition de la fonction SetABTest sur l'objet window
    globalThis.SetABTest = (choixAbTest: string) => {
      handleAbTestEvent(choixAbTest, userSheetId, saveResponse)
    }

    // Traiter les appels en attente
    if (globalThis._abTestQueue) {
      globalThis._abTestQueue.forEach((choix) => {
        handleAbTestEvent(choix, userSheetId, saveResponse)
      })
      delete globalThis._abTestQueue
    }
  }, [saveResponse, userSheetId])

  useEffect(() => {
    // Écoute quand l'iframe est prête et lui envoie les vars
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'tc_privacy_center_ready') {
        const iframe = document.getElementById('privacy-iframe') as HTMLIFrameElement | null
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: 'tc_brand_vars',
              brand_color_primary:
                'brand_color_primary' in globalThis.window.tc_vars
                  ? globalThis.window.tc_vars.brand_color_primary
                  : getMainColor(template),
              brand_font:
                'brand_font' in globalThis.window.tc_vars
                  ? globalThis.window.tc_vars.brand_font
                  : getFont(template),
              brand_cta_text_color:
                'brand_color_cta_text' in globalThis.window.tc_vars
                  ? globalThis.window.tc_vars.brand_color_cta_text
                  : getSecondaryColor(template),
            },
            'https://cdn.trustcommander.net',
          )
        }
      }
    }
    globalThis.addEventListener('message', handleMessage)
    return () => {
      globalThis.removeEventListener('message', handleMessage)
    }
  })

  if (currentSlide === undefined)
    return (
      <ThemeProvider
        appearance={finalAppearance}
        template={(getParamCaseInsensitive(queryParams, 'template') as 'mtx' | 'lca') ?? template}
        isWidget={isWidgetMode}
        isProduction={isProduction}
        apiUrl={import.meta.env.VITE_API_URL}
        formsUrl={getUrl(import.meta.env.VITE_ENV, 'forms', appearance)}
      >
        <Slides toggleNextIsLoading={toggleNextIsLoading} />
      </ThemeProvider>
    )

  return (
    <ThemeProvider
      appearance={finalAppearance}
      template={(getParamCaseInsensitive(queryParams, 'template') as 'mtx' | 'lca') ?? template}
      isWidget={isWidgetMode}
      isProduction={isProduction}
      apiUrl={import.meta.env.VITE_API_URL}
      formsUrl={getUrl(import.meta.env.VITE_ENV, 'forms', appearance)}
    >
      {!isWidgetMode && (
        <>
          <title>{meta?.title}</title>
          <link rel="icon" href={finalAppearance?.faviconUrl ?? `/favicon-${template}.ico`} />
          {meta?.description && <meta name="description" content={meta.description} />}
          {meta?.robots && <meta name="robots" content={meta.robots} />}
          {meta?.canonical && <link rel="canonical" href={meta.canonical} />}
        </>
      )}
      <div>
        <ScrollNavigationIndicator progress={scrollProgress} direction={scrollDirection} />
        {!isWidgetMode && <Header argumentMarketing={argumentMarketing} />}

        {device === 'Mobile' && appearance.stepperPosition !== 'none' && (
          <Stepper stepper={stepper} />
        )}

        {device === 'Desktop' && showTopProgressBar(appearance.stepperPosition) && (
          <Container>
            <Row style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              <Col col={12} style={{ position: 'relative' }}>
                <Stepper stepper={stepper} variant="progressbar" />
              </Col>
            </Row>
          </Container>
        )}

        <Container
          style={{
            marginTop: device === 'Mobile' ? '36px' : '32px',
            marginBottom: device === 'Mobile' ? '40px' : '32px',
            ...getContentContainerStyle(device === 'Desktop', appearance.isVerticallyCentered),
          }}
        >
          <Row
            justifyContent="center"
            style={getContentRowStyle(device === 'Desktop', appearance.isVerticallyCentered)}
          >
            {device === 'Desktop' && showSideStepper(appearance.stepperPosition) && (
              <Col
                col={12}
                m={getWidthStepper(appearance.stepperPosition)}
                style={{ position: 'relative' }}
              >
                <Stepper stepper={stepper} />
              </Col>
            )}
            <Col col={12} m={7}>
              {device === 'Mobile' && currentSlide.id === 1 && argumentMarketing && (
                <Row
                  style={{
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginBottom: '44px',
                    marginTop: '0',
                  }}
                >
                  <Col col={12} style={{ position: 'relative' }} gap={0}>
                    <MarketingHulk argumentMarketing={argumentMarketing} />
                  </Col>
                </Row>
              )}
              <Slides toggleNextIsLoading={toggleNextIsLoading} />
              <CtaWrapper>
                <Row style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                  <Col
                    col={12}
                    style={{ position: 'relative', marginTop: '0px', marginBottom: '0px' }}
                  >
                    <ValidateSlideButtons
                      nextLabel={getNextButtonLabel(
                        currentSlide,
                        appearance.lastCtaLabel,
                        appearance.ctaLabel,
                      )}
                      nextDisabled={false}
                      nextLoading={nextIsLoading}
                      nextForm="stepForm"
                      nextDisplayed={currentSlide.displayNextButton}
                      isLastNext={currentSlide.isLastSlide}
                      onPrev={getPrevHandler(canPrev, goPrev)}
                    />
                  </Col>
                </Row>
                {device === 'Mobile' && !isWidgetMode && (
                  <Row style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                    <Col
                      col={12}
                      style={{ position: 'relative', marginTop: '0px', marginBottom: '0px' }}
                    >
                      <ButtonSaveForLater onClick={() => setSaveModalOpened(true)} hidden={false} />
                    </Col>
                  </Row>
                )}
              </CtaWrapper>
            </Col>
          </Row>
          {((device === 'Mobile' && currentSlide.id === 1) || device === 'Desktop') &&
            mentions &&
            !isWidgetMode && (
              <Row style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <Col
                  col={12}
                  style={{ position: 'relative', marginTop: '0px', marginBottom: '0px' }}
                >
                  <Mentions mentions={mentions} />
                </Col>
              </Row>
            )}
        </Container>
      </div>

      {!isWidgetMode && (
        <>
          <Footer
            footerContent={appearance.footerContent?.map((item) => ({
              ...item,
              picto: item.picto as IconType,
            }))}
            isFooterDisplayMobile={appearance.isFooterDisplayMobile}
          />
          <InternauteReconnu userSheetId={userSheetId} />
          <SaveAndContinueLater
            open={saveModalOpened}
            onClose={() => {
              setSaveModalOpened(false)
            }}
          />
        </>
      )}

      <TransfertProduit />
    </ThemeProvider>
  )
}
